import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TableColumnModel } from '../../models/table-column.model';
import { ControlValueAccessor } from '@angular/forms';
import { TableColumnInputEnum } from '../../enums/table-column-input-type.enum';
import { getProvider } from '../../models/control-value-accessor.model';
import { Subscription, filter } from 'rxjs';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TableColumnFormatsEnum } from '../../enums/table-column-formats.enum';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ActionKeys } from '../../enums/keys.enum';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { MenuItem } from 'primeng/api';

export enum TablePlaceholders {
    NULL_PLACEHOLDER = '--'
}

/**
 * ### Accessibility Usage
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***rowSelectedAriaLabel*** - use this if any table cell can be selected, and this is used to provide more description for selection
 */
@Component({
    selector: 'agpr-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    providers: [getProvider(TableComponent)],
    standalone: false
})
export class TableComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
    public readonly columnTypes = TableColumnInputEnum;
    public readonly rowFormats = TableColumnFormatsEnum;
    public readonly tablePlaceholders = TablePlaceholders;
    public readonly messageSeverity = messageSeverityEnum;

    @Input() id: string;
    @Input() columns: TableColumnModel[];

    @Input() showDelete: boolean = false;
    @Input() deleteLabel: string = 'Core.button.delete';
    @Input() showView: boolean = false;
    /** Collapses the row's actions into a labelled menu rather than a row of icon buttons */
    @Input() actionMenu: boolean = false;
    /**
     * The caller's own items for the row menu, listed after View and Delete. Each command is handed
     * the row it was opened from as `event.data`. Only drawn with {@link actionMenu}
     */
    @Input() extraActions: MenuItem[] = [];
    /** Whether the menu's View and Delete items show their icons - extra actions always keep their own */
    @Input() actionMenuIcons: boolean = true;
    /** Set to 'right' to right align the action menu's items */
    @Input() actionMenuTextAlignment: string;
    @Input() viewIcon: string = 'fa-regular fa-duotone fa-eye';
    @Input() selectableRow: boolean = false;
    @Input() showHeader: boolean = true;
    @Input() viewIconTooltip: string = 'Tags.manageDialog.viewIconTooltip';
    @Input() rowSelectedAriaLabel: string;
    @Input() set sortColumn(column: TableColumnModel) {
        this.currentSortColumn = column;
        this.sort();
    }
    @Input() set sortAscending(ascending: boolean) {
        this.currentSortAscending = ascending;
        this.sort();
    }
    @Input() initiallyFocusable: boolean = true;

    @Input() vsItemSize = 20;
    @Input() vsMinBufferPx = 20000000;
    @Input() vsMaxBufferPx = 40000000;
    @Input() enableSelectAll = true;
    @Input() noContentMessage: string;
    @Input() highlightedRow: any;

    @Output() itemChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemDelete: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemView: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemSelected: EventEmitter<any> = new EventEmitter<any>();
    @Output() highlightedRowChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() allSelectedChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    rawData: any[];
    sortedData: any[];

    editingColumn: TableColumnModel;
    editingRow: any;

    currentSortColumn: TableColumnModel;
    currentSortAscending: boolean;

    selectedRow: any;

    onChange: Function;
    onTouched: Function;

    userPreferences: PreferencesModel;
    defaultDateFormat: string;

    hasAccountUsers: boolean;
    showActionCell: boolean;
    /** The row menu's items, built once from whichever actions the caller has switched on */
    actionMenuOptions: MenuItem[] = [];
    deletedItemIndex: number = -1;

    actionItemWidth: string;
    selectAll: boolean = false;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly focusableElementSelectors: string = 'button, [href], input, select, textarea, [tabindex]';

    @ViewChild('tableHeader') tableHeaderViewChild: ElementRef;
    @ViewChild(CdkVirtualScrollViewport) virtualScrollBody: CdkVirtualScrollViewport;
    virtualScrollSubscription: Subscription;

    constructor (
        protected currentUserService: CurrentUserStateService,
        protected usersService: UsersService
    ) {

    }
    ngOnDestroy(): void {
        if (this.virtualScrollSubscription) {
            this.virtualScrollSubscription.unsubscribe();
        }
    }

    writeValue(data: any[]): void {
        this.selectAll = false;
        this.rawData = data;
        this.sort();
    }
    registerOnChange(fn: Function): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: Function): void {
        this.onTouched = fn;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserService
                .getPreferences()
                .pipe(filter(x => !!x))
                .subscribe(
                    result => {
                        this.userPreferences = result;
                        this.defaultDateFormat = this.userPreferences.getDateFormatString(true, true, true);
                    }),
            this.currentUserService
                .getAccountUsers()
                .subscribe(
                    result => {
                        const hasAccountUsers: boolean = Array.isArray(result) && result.length > 0;

                        this.hasAccountUsers = true;
                    }
                )
        );
        this.calculateActionItemWidth();
        this.setUpActions();
    }

    /** Extra actions can arrive after init, once whatever decides them has loaded */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.extraActions && !changes.extraActions.firstChange) {
            this.setUpActions();
        }
    }


    ngAfterViewInit() {
        this.virtualScrollSubscription =  this.virtualScrollBody.renderedRangeStream.subscribe(range => {
            if (this.tableHeaderViewChild) {
                const top = range.start * this.vsItemSize * -1;
                this.tableHeaderViewChild.nativeElement.style.top = top + 'px';
            }
        });
    }

    /**
     * Starts editing for a row+column
     */
    editField(column: TableColumnModel, row: any, event: Event): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.editingColumn = column;
        this.editingRow = row;
        event.stopPropagation();
    }
    /**
     * Sets the state of a checkbox field
     */
    saveCheckboxValue(checked: boolean, column: TableColumnModel, row: any): void {
        if (column.type === TableColumnInputEnum.checkbox) {
            row[column.field] = checked;
        } else if (column.type === TableColumnInputEnum.text) {
            row[column.selectField] = checked;
        }
        this.itemChanged.emit(row);
        this.onChange(this.rawData);
    }

    toggleSelectAll(): void {
        this.rawData.forEach(item => item.selected = this.selectAll);
        this.onChange(this.rawData);
        this.allSelectedChanged.next(this.selectAll);
    }

    /**
     * Onchange for the tags changing
     */
    tagsUpdated(event: any, column: TableColumnModel, row: any): void {
        this.itemChanged.emit(row);
        this.onChange(this.rawData);
    }

    /**
     * Allows saving when the user presses enter while editing a field
     */
    saveOnEnter(keyDownEvent: KeyboardEvent, column: TableColumnModel, row: any): void {
        if (keyDownEvent.key === ActionKeys.ENTER) {
            this.saveValue(column, row);
        }
        if (keyDownEvent.stopPropagation) {
            keyDownEvent.stopPropagation();
        }
    }

    /**
     * Saves a value from an edited field
     */
    saveValue(column: TableColumnModel, row: any): void {
        switch (column.type) {
            case TableColumnInputEnum.text:
                this.saveTextValue(column, row);
                break;
            case TableColumnInputEnum.textarea:
                this.saveTextValue(column, row);
                break;
            default:
                break;
        }
    }

    /**
     * Saves a text value from an edited field
     */
    saveTextValue(column: TableColumnModel, row: any): void {
        this.editingColumn = null;
        this.editingRow = null;
        row[column.field] = row[column.field].trim();

        this.itemChanged.emit(row);
        this.onChange(this.rawData);
    }

    private setUpActions(): void {
        this.showActionCell = this.showView || this.showDelete || (this.actionMenu && !!this.extraActions?.length);
        this.buildActionMenu();
    }

    /** One entry per action in play, each acting on the row the menu was opened from */
    private buildActionMenu(): void {
        if (!this.actionMenu) {
            return;
        }

        this.actionMenuOptions = [
            this.showView
                ? {
                    label: this.viewIconTooltip,
                    icon: this.actionMenuIcons ? this.viewIcon : null,
                    command: event => this.view(event.data)
                }
                : null,
            this.showDelete
                ? {
                    label: this.deleteLabel,
                    icon: this.actionMenuIcons ? 'fa-regular fa-duotone fa-trash-can' : null,
                    command: event => this.deleteItem(event.data, this.rawData?.indexOf(event.data))
                }
                : null,
            ...(this.extraActions ?? [])
        ].filter(option => !!option);
    }

    /**
     * Notifies that an item is being deleted
     */
    deleteItem(row: any, rowIndex: number): void {
        this.deletedItemIndex = rowIndex;
        this.itemDelete.emit(row);
    }

    /**
     * Notifies that an item is wanting to be 'viewed' in more detail
     */
    view(row: any): void {
        this.itemView.emit(row);
    }

    /**
     * Toggles the sorting when a user clicks on a sortable column
     */
    toggleSort(column: TableColumnModel): void {
        if (!column.sortable) {
            return;
        }

        if (this.currentSortColumn === column) {
            this.currentSortAscending = !this.currentSortAscending;
        } else {
            this.currentSortAscending = true;
            this.currentSortColumn = column;
        }

        this.sort();
    }

    /**
     * Sorts the table based on the currently selected sort column and order
     */
    sort(): void {
        this.sortedData = (this.rawData || []).slice();

        if (!this.currentSortColumn) {
            this.setInitialFocus();
            return;
        }

        this.sortedData.sort((a, b) => {
            return this.compareRows(this.currentSortColumn, a, b);
        });

        if (!this.currentSortAscending) {
            this.sortedData = this.sortedData.reverse();
        }
        this.setInitialFocus();
    }

    /**
     * Compares rows based on column sorting definition
     */
    compareRows(column: TableColumnModel, a: any, b: any): 0 | 1 | -1 {
        let colA = this.getSortValue(a, column);
        let colB = this.getSortValue(b, column);

        if (column.sortType === 'text') {
            colA = ('' + colA).toLowerCase();
            colB = ('' + colB).toLowerCase();

            return colA.localeCompare(colB);
        }

        // numeric (default)
        colA = +colA || 0;
        colB = +colB || 0;

        return colA > colB ? 1 : colA < colB ? -1 : 0;
    }

    /**
     * Selects a row in the single selection mode
     */
    onRowClick(row: any, event?: any): void {
        if (this.selectableRow) {
            this.selectedRow = row;
            this.itemSelected.emit(row);
        }
        this.highlightedRow = row;
        this.highlightedRowChanged.emit(row);
    }

    private getSortValue(row: any, column: TableColumnModel): any {
        return row[column.sortField || column.field];
    }

    calculateActionItemWidth(): void {
        // The menu is one kebab button however many actions it holds, so tables sharing a layout line up
        if (this.actionMenu) {
            this.actionItemWidth = '80px';
            return;
        }

        let width: number = 0;

        if (this.showView) {
            width += 35;
        }

        if (this.showDelete) {
            width += 70;
        }

        this.actionItemWidth = width + 'px';
    }

    // define header cell as column header, action cell as the table cell that contains view or delete button
    // all the rest cells are data cells
    handleHeaderKeyboardNavigation(event: KeyboardEvent, column: any, colIdx: number) {
        let columnIndex = colIdx;
        switch (event.key) {
            case ActionKeys.RIGHT:
                if (columnIndex < this.columns.length - 1) {
                    columnIndex++;
                    this.focusHeader(columnIndex);
                } else {
                    this.focusActionHeader();
                }
                break;
            case ActionKeys.DOWN:
                if (column === 'actionHeader') {
                    this.moveActionCellFocus(0, true);
                } else {
                    this.moveDataCellFocus(0, columnIndex, true);
                }
                return;
            case ActionKeys.LEFT:
                if (column === 'actionHeader') {
                    this.focusHeader(this.columns.length - 1);
                    return;
                }
                if (columnIndex > 0) {
                    columnIndex--;
                }
                this.focusHeader(columnIndex);
                break;
            case ActionKeys.ENTER:
            case ActionKeys.SPACE:
                if (column.sortable && (event.target as HTMLElement).nodeName !== 'INPUT') {
                    this.toggleSort(column);
                }
                break;
        }
    }

    handleDataCellKeyboardNavigation(event: KeyboardEvent, rowIdx: number, colIdx: number): void {
        let rowIndex = rowIdx, columnIndex = colIdx;
        switch (event.key) {
            case ActionKeys.UP:
                if (this.showHeader || rowIndex > 0) {
                    this.moveDataCellFocus(rowIndex, columnIndex, false);
                }
                rowIndex--;
                if (rowIndex === -1) {
                    this.focusHeader(columnIndex);
                } else {
                    this.moveDataCellFocus(rowIndex, columnIndex, true);
                }
                break;
            case ActionKeys.RIGHT:
                this.moveDataCellFocus(rowIndex, columnIndex, false);
                columnIndex++;
                if (columnIndex === this.columns.length) {
                    if (this.showActionCell) {
                        this.moveActionCellFocus(rowIndex, true);
                    } else {
                        return;
                    }
                } else {
                    this.moveDataCellFocus(rowIndex, columnIndex, true);
                }
                break;
            case ActionKeys.DOWN:
                if (rowIndex < this.sortedData.length - 1) {
                    this.moveDataCellFocus(rowIndex, columnIndex, false);
                    rowIndex++;
                    this.moveDataCellFocus(rowIndex, columnIndex, true);
                }
                break;
            case ActionKeys.LEFT:
                if (columnIndex > 0) {
                    this.moveDataCellFocus(rowIndex, columnIndex, false);
                    columnIndex--;
                    this.moveDataCellFocus(rowIndex, columnIndex, true);
                }
                break;
            case ActionKeys.ENTER:
            case ActionKeys.SPACE:
                if (this.selectableRow) {
                    this.onRowClick(this.sortedData[rowIndex]);
                    return;
                }
                break;
        }
    }

    handleActionCellKeyboardNavigation(event: KeyboardEvent, rowIdx: number): void {
        let rowIndex = rowIdx;
        switch (event.key) {
            case ActionKeys.UP:
                this.moveActionCellFocus(rowIndex, false);
                if (rowIndex > 0) {
                    rowIndex--;
                } else {
                    this.focusActionHeader();
                    return;
                }
                this.moveActionCellFocus(rowIndex, true);
                break;
            case ActionKeys.DOWN:
                if (rowIndex < this.sortedData.length - 1) {
                    this.moveActionCellFocus(rowIndex, false);
                    rowIndex++;
                }
                this.moveActionCellFocus(rowIndex, true);
                break;
            case ActionKeys.LEFT:
                this.moveActionCellFocus(rowIndex, false);
                this.moveDataCellFocus(rowIndex, this.columns.length - 1, true);
                return;
        }
    }

    private focusHeader(columnIndex: number): void {
        const headerCell = document.getElementById(`${this.id}_tableHeader_${columnIndex}`);
        const firstFocusableElement = headerCell.querySelectorAll(this.focusableElementSelectors)[0] as HTMLElement;
        if (firstFocusableElement) {
            firstFocusableElement.focus();
        } else {
            headerCell.focus();
        }
    }

    private focusActionHeader(): void {
        document.getElementById(`${this.id}_tableActionHeader`).focus();
    }

    private moveDataCellFocus(rowIndex: number, columnIndex: number, isTargetCell: boolean): void {
        const dataCell = this.getTableDataCell(rowIndex, columnIndex);
        // TODO: dataCell can be undefined when working with virtual scrolling, see D24199
        const tableCell = this.changeCellFocusability(dataCell, isTargetCell);
        if (tableCell) {
            tableCell.focus();
        }
    }

    private moveActionCellFocus(rowIndex: number, isTargetCell: boolean) {
        const actionCell = document.getElementById(`${this.id}_tableActionCell_${rowIndex}`);
        const nextFocusableCell = this.changeCellFocusability(actionCell, isTargetCell);
        if (nextFocusableCell) {
            nextFocusableCell.focus();
        }
    }

    // if enable is true, then it means the focus moves TO this cell, otherwise it means move focus FROM this cell
    private changeCellFocusability(cell: HTMLElement, enable: boolean): HTMLElement {
        if (!cell) {
            return;
        }
        const focusableElements = cell.querySelectorAll(this.focusableElementSelectors);
        let target: HTMLElement = null;
        if (focusableElements.length > 0) {
            // the cell has interactable elememnts in it, should focus on the first such element
            focusableElements.forEach((focusableElement, index) => {
                focusableElement.setAttribute('tabindex', enable ? '0' : '-1');
                if (enable && index === 0) {
                    target = focusableElement as HTMLElement;
                }
            });
        } else {
            // the cell has plain content, should focus on the cell
            cell.setAttribute('tabindex', enable ? '0' : '-1');
            if (enable) {
                target = cell;
            }
        }

        return target;
    }

    protected setInitialFocus(): void {
        requestAnimationFrame(() => {
            if (!this.showHeader) {
                this.getTableDataCell(0, 0)?.setAttribute('tabindex', '0');
            }
            if (this.deletedItemIndex > -1) {
                this.moveDataCellFocus(this.deletedItemIndex, 0, true);
                this.deletedItemIndex = -1;
            }
        });
    }

    private getTableDataCell(rowIndex: number, columnIndex: number): HTMLElement {
        return document.getElementById(`${this.id}_tableDataCell_${rowIndex}_${columnIndex}`);
    }
}
